<template>
  <div class="apikey-page">
    <a-spin :spinning="loading">
      <div class="apikey-container">
        <div class="page-header">
          <div class="header-icon"><SafetyCertificateOutlined /></div>
          <div class="header-text">
            <h2>API Key</h2>
            <p>用于脚本或第三方工具安全调用泰坦系统接口，无需暴露账号密码</p>
          </div>
        </div>
        <!-- 空状态 -->
        <div v-if="!keyInfo" class="empty-state">
          <div class="empty-card">
            <div class="empty-key-icon"><KeyOutlined /></div>
            <p class="empty-tip">当前账号尚未生成 API Key</p>
            <p class="empty-sub">每个账号仅支持一个启用的 Key，重新生成将使旧 Key 立即失效</p>
            <a-button type="primary" size="large" class="gen-btn" @click="openGenerateModal">
              <template #icon><PlusOutlined /></template>
              生成 API Key
            </a-button>
          </div>
        </div>
        <!-- 已有 Key -->
        <div v-else class="key-detail">
          <div class="key-card">
            <div class="key-card-header">
              <span class="key-label">当前 API Key</span>
              <a-tag :color="statusColor" class="status-tag">{{ statusText }}</a-tag>
            </div>
            <div class="key-value-row">
              <span class="key-value">{{ keyInfo.keyPrefix }}<span class="mask">****</span></span>
            </div>
            <a-divider style="margin: 16px 0" />
            <div class="key-meta">
              <div class="meta-item">
                <span class="meta-label">有效期至</span>
                <span class="meta-value" :class="{ expired: isExpired }">{{ formatExpire(keyInfo.expireTime) }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">创建时间</span>
                <span class="meta-value">{{ keyInfo.createTime || '-' }}</span>
              </div>
            </div>
            <div class="key-actions">
              <a-space>
                <a-button @click="openGenerateModal">
                  <template #icon><ReloadOutlined /></template>
                  重新生成
                </a-button>
                <a-button danger @click="handleRevoke">
                  <template #icon><DeleteOutlined /></template>
                  吊销
                </a-button>
              </a-space>
            </div>
          </div>
        </div>
      </div>
    </a-spin>
    <!-- 弹窗1: 配置 + 生成 -->
    <a-modal
      v-model:visible="configVisible"
      :title="keyInfo ? '重新生成 API Key' : '生成 API Key'"
      :width="520"
      centered
      :mask-closable="false"
      :destroy-on-close="true"
      ok-text="确认生成"
      cancel-text="取消"
      :confirm-loading="generating"
      @ok="doGenerate"
    >
      <div class="step-config">
        <p class="step-label">选择有效期</p>
        <div class="expire-options">
          <div
            v-for="opt in expireOptions"
            :key="opt.value"
            class="expire-card"
            :class="{ active: expireDays === opt.value }"
            @click="expireDays = opt.value"
          >
            <CheckCircleFilled v-if="expireDays === opt.value" class="expire-check" />
            <span class="expire-title">{{ opt.label }}</span>
            <span class="expire-desc">{{ opt.desc }}</span>
          </div>
        </div>
        <a-alert
          v-if="keyInfo"
          message="重新生成后，旧 Key 将立即失效且无法恢复。"
          type="warning"
          show-icon
          style="margin-top: 20px"
        />
      </div>
    </a-modal>
    <!-- 弹窗2: 结果(展示完整 Key 供复制) -->
    <a-modal
      v-model:visible="resultVisible"
      title="API Key 已生成"
      :width="520"
      centered
      :mask-closable="false"
      :closable="false"
      :cancel-button-props="{ style: { display: 'none' } }"
      ok-text="我已保存"
      @ok="tryCloseResult"
    >
      <div class="step-result">
        <div class="result-success">
          <CheckCircleFilled class="result-icon" />
          <span class="result-text">生成成功，完整 Key 已自动复制到剪贴板</span>
        </div>
        <a-alert
          message="关闭后将不再显示完整 Key，请确认已妥善保存。"
          type="warning"
          show-icon
          style="margin-bottom: 16px"
        />
        <div class="key-display">
          <span class="key-full">{{ generatedKey }}</span>
          <a-button :type="hasCopied ? 'default' : 'primary'" @click="copyKey">
            <template #icon>
              <CheckOutlined v-if="hasCopied" />
              <CopyOutlined v-else />
            </template>
            {{ hasCopied ? '已复制' : '复制' }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>
<script>
import { message, Modal } from 'ant-design-vue';
import {
  SafetyCertificateOutlined,
  KeyOutlined,
  CopyOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons-vue';
import { generateApiKey, getApiKeyInfo, revokeApiKey } from '@/api/systemManage/apikey';
import { createVNode } from 'vue';
export default {
  components: {
    SafetyCertificateOutlined,
    KeyOutlined,
    CopyOutlined,
    PlusOutlined,
    ReloadOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    CheckOutlined,
  },
  data() {
    return {
      loading: false,
      keyInfo: null,
      configVisible: false,
      resultVisible: false,
      expireDays: 0,
      generatedKey: '',
      generating: false,
      hasCopied: false,
    };
  },
  computed: {
    expireOptions() {
      return [
        { value: 0, label: '永久有效', desc: '不会过期，除非手动吊销' },
        { value: 7, label: '7 天', desc: '适合短期调试' },
        { value: 30, label: '30 天', desc: '一个月有效期' },
        { value: 90, label: '90 天', desc: '一个季度有效期' },
      ];
    },
    statusColor() {
      if (this.isExpired) return 'red';
      return this.keyInfo.status === 1 ? 'green' : 'default';
    },
    statusText() {
      if (this.isExpired) return '已过期';
      return this.keyInfo.status === 1 ? '启用中' : '已停用';
    },
    isExpired() {
      if (!this.keyInfo || !this.keyInfo.expireTime) return false;
      return new Date(this.keyInfo.expireTime).getTime() < Date.now();
    },
  },
  mounted() {
    this.loadKeyInfo();
  },
  methods: {
    async loadKeyInfo() {
      this.loading = true;
      try {
        const res = await getApiKeyInfo();
        this.keyInfo = res.code === 0 && res.data ? res.data : null;
      } catch {
        this.keyInfo = null;
      } finally {
        this.loading = false;
      }
    },
    openGenerateModal() {
      this.expireDays = 0;
      this.configVisible = true;
    },
    async doGenerate() {
      this.generating = true;
      try {
        const params = { expireDays: this.expireDays || null };
        const res = await generateApiKey(params);
        if (res.code === 0 && res.key) {
          this.generatedKey = res.key;
          this.hasCopied = false;
          this.configVisible = false;
          this.resultVisible = true;
          this.autoCopyKey();
        } else {
          message.error(res.msg || '生成失败');
        }
      } catch {
        message.error('生成失败，请稍后重试');
      } finally {
        this.generating = false;
      }
    },
    autoCopyKey() {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.generatedKey).then(() => {
          this.hasCopied = true;
          message.success('已自动复制到剪贴板');
        }).catch(() => {
          message.warning('自动复制失败，请手动点击复制按钮');
        });
      } else {
        message.warning('浏览器不支持自动复制，请手动点击复制按钮');
      }
    },
    copyKey() {
      navigator.clipboard.writeText(this.generatedKey).then(() => {
        this.hasCopied = true;
        message.success('已复制到剪贴板');
      }).catch(() => {
        message.error('复制失败，请手动选中 Key 文本复制');
      });
    },
    tryCloseResult() {
      if (!this.hasCopied) {
        Modal.confirm({
          title: '尚未复制 Key',
          icon: createVNode(ExclamationCircleOutlined),
          content: '当前 Key 尚未复制，关闭后将无法再次查看。确定关闭吗？',
          okText: '确定关闭',
          okType: 'danger',
          cancelText: '再等等',
          onOk: () => this.handleResultClose(),
        });
      } else {
        this.handleResultClose();
      }
    },
    handleResultClose() {
      this.resultVisible = false;
      this.generatedKey = '';
      this.hasCopied = false;
      this.loadKeyInfo();
    },
    handleRevoke() {
      Modal.confirm({
        title: '吊销 API Key',
        icon: createVNode(ExclamationCircleOutlined),
        content: '吊销后，使用该 Key 的所有请求将立即被拒绝。确认继续？',
        okText: '确认吊销',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          try {
            const res = await revokeApiKey();
            if (res.code === 0) {
              message.success('已吊销');
              this.keyInfo = null;
            }
          } catch {
            message.error('操作失败');
          }
        },
      });
    },
    formatExpire(expireTime) {
      if (!expireTime) return '永久有效';
      return expireTime;
    },
  },
};
</script>
<style lang="less" scoped>
.apikey-page {
  padding: 24px;
  .apikey-container {
    max-width: 640px;
    margin: 0 auto;
  }
  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 32px;
    .header-icon {
      width: 52px;
      height: 52px;
      border-radius: 12px;
      background: linear-gradient(135deg, #1890ff, #096dd9);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
      color: #fff;
      margin-right: 16px;
      flex-shrink: 0;
    }
    .header-text {
      h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 4px;
        color: #1a1a1a;
      }
      p {
        font-size: 13px;
        color: #888;
        margin: 0;
      }
    }
  }
  .empty-state {
    .empty-card {
      background: #fff;
      border: 1px dashed #d9d9d9;
      border-radius: 12px;
      padding: 56px 32px;
      text-align: center;
      transition: border-color 0.3s;
      &:hover {
        border-color: #1890ff;
      }
      .empty-key-icon {
        font-size: 48px;
        color: #bbb;
        margin-bottom: 20px;
      }
      .empty-tip {
        font-size: 16px;
        color: #333;
        font-weight: 500;
        margin-bottom: 8px;
      }
      .empty-sub {
        font-size: 13px;
        color: #999;
        margin-bottom: 28px;
        line-height: 1.6;
      }
      .gen-btn {
        border-radius: 6px;
      }
    }
  }
  .key-detail {
    .key-card {
      background: #fff;
      border: 1px solid #f0f0f0;
      border-radius: 12px;
      padding: 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
      .key-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
        .key-label {
          font-size: 14px;
          color: #666;
        }
        .status-tag {
          border-radius: 4px;
        }
      }
      .key-value-row {
        .key-value {
          font-family: 'SFMono-Regular', Consolas, Menlo, monospace;
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
          word-break: break-all;
          .mask {
            color: #ccc;
            letter-spacing: 3px;
          }
        }
      }
      .key-meta {
        display: flex;
        gap: 48px;
        .meta-item {
          .meta-label {
            display: block;
            font-size: 12px;
            color: #999;
            margin-bottom: 4px;
          }
          .meta-value {
            font-size: 14px;
            color: #333;
            &.expired {
              color: #ff4d4f;
            }
          }
        }
      }
      .key-actions {
        margin-top: 20px;
        text-align: right;
      }
    }
  }
}
.step-config {
  .step-label {
    font-weight: 500;
    margin-bottom: 16px;
    color: #333;
  }
  .expire-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .expire-card {
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.25s;
    position: relative;
    &:hover {
      border-color: #1890ff;
      background: #f0f8ff;
    }
    &.active {
      border-color: #1890ff;
      background: #e6f7ff;
    }
    .expire-check {
      position: absolute;
      top: 10px;
      right: 10px;
      color: #1890ff;
      font-size: 16px;
    }
    .expire-title {
      display: block;
      font-size: 15px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 4px;
    }
    .expire-desc {
      display: block;
      font-size: 12px;
      color: #999;
    }
  }
}
.step-result {
  .result-success {
    text-align: center;
    margin-bottom: 20px;
    .result-icon {
      font-size: 42px;
      color: #52c41a;
      margin-bottom: 8px;
    }
    .result-text {
      display: block;
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }
  .key-display {
    display: flex;
    align-items: center;
    gap: 8px;
    .key-full {
      flex: 1;
      font-family: 'SFMono-Regular', Consolas, Menlo, monospace;
      font-size: 14px;
      color: #1a1a1a;
      background: #f5f5f5;
      border: 1px solid #e8e8e8;
      border-radius: 6px;
      padding: 8px 12px;
      word-break: break-all;
      user-select: all;
      line-height: 1.6;
    }
  }
}
</style>
